import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trash2, Upload, Plus, Save, ImageOff } from "lucide-react";

export const Route = createFileRoute("/tools/brands")({
  head: () => ({
    meta: [
      { title: "Brand Manager | Apex AV" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: BrandsAdmin,
});

type Brand = {
  id: string;
  slug: string;
  name: string;
  service_path: string;
  service_paths: string[];
  logo_url: string | null;
  logo_height: number;
  sort_order: number;
  enabled: boolean;
  include_on_letter: boolean;
};

const SERVICE_PATHS = [
  "/services/whole-home-audio",
  "/services/home-theater",
  "/services/whole-home-video",
  "/services/tv-mounting",
  "/services/outdoor",
  "/services/networking",
  "/services/climate-control",
  "/services/security",
  "/services/smart-home",
  "/services/control4",
  "/services/lighting",
  "/services/shading",
  "/services/hidden-audio",
  "/services/conference",
  "/services/prewire",
  "/services/retrofit",
];

function BrandsAdmin() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("brands")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) {
      toast.error(error.message);
    } else {
      setBrands(data as Brand[]);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function addBrand() {
    const slug = prompt("New brand slug (lowercase, unique, e.g. 'bose'):");
    if (!slug) return;
    const clean = slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-");
    const maxSort = brands.reduce((m, b) => Math.max(m, b.sort_order), 0);
    const { error } = await supabase.from("brands").insert({
      slug: clean,
      name: clean.charAt(0).toUpperCase() + clean.slice(1),
      service_path: "/services/whole-home-audio",
      service_paths: ["/services/whole-home-audio"],
      sort_order: maxSort + 10,
      enabled: true,
    });
    if (error) toast.error(error.message);
    else {
      toast.success("Brand added");
      load();
    }
  }

  async function updateBrand(id: string, patch: Partial<Brand>) {
    setSavingId(id);
    const { error } = await supabase.from("brands").update(patch).eq("id", id);
    setSavingId(null);
    if (error) {
      toast.error(error.message);
      return false;
    }
    setBrands((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
    return true;
  }

  async function deleteBrand(id: string, name: string) {
    if (!confirm(`Delete "${name}"?`)) return;
    const { error } = await supabase.from("brands").delete().eq("id", id);
    if (error) toast.error(error.message);
    else {
      toast.success("Deleted");
      setBrands((prev) => prev.filter((b) => b.id !== id));
    }
  }

  async function uploadLogo(brand: Brand, file: File) {
    const ext = file.name.split(".").pop()?.toLowerCase() || "png";
    const path = `${brand.slug}-${Date.now()}.${ext}`;
    setSavingId(brand.id);
    const { error: upErr } = await supabase.storage
      .from("brand-logos")
      .upload(path, file, { upsert: true, contentType: file.type });
    if (upErr) {
      setSavingId(null);
      toast.error(upErr.message);
      return;
    }
    // Long-lived signed URL (10 years) — private bucket, anon SELECT policy still requires signed access via public URL.
    const { data: signed, error: sErr } = await supabase.storage
      .from("brand-logos")
      .createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
    setSavingId(null);
    if (sErr || !signed) {
      toast.error(sErr?.message ?? "Failed to sign URL");
      return;
    }
    await updateBrand(brand.id, { logo_url: signed.signedUrl });
    toast.success("Logo uploaded");
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-foreground">Brand Manager</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage the brand strip shown across the site. Upload a logo and pick which service page each brand links to.
          </p>
        </div>
        <Button onClick={addBrand}>
          <Plus className="mr-2 h-4 w-4" /> Add brand
        </Button>
      </div>

      {loading ? (
        <p className="mt-10 text-muted-foreground">Loading…</p>
      ) : (
        <div className="mt-8 space-y-3">
          {brands.map((b) => (
            <BrandRow
              key={b.id}
              brand={b}
              busy={savingId === b.id}
              onUpdate={(patch) => updateBrand(b.id, patch)}
              onDelete={() => deleteBrand(b.id, b.name)}
              onUpload={(file) => uploadLogo(b, file)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BrandRow({
  brand,
  busy,
  onUpdate,
  onDelete,
  onUpload,
}: {
  brand: Brand;
  busy: boolean;
  onUpdate: (patch: Partial<Brand>) => Promise<boolean>;
  onDelete: () => void;
  onUpload: (file: File) => void;
}) {
  const [local, setLocal] = useState(brand);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => setLocal(brand), [brand]);

  const dirty =
    local.name !== brand.name ||
    local.slug !== brand.slug ||
    local.service_path !== brand.service_path ||
    local.logo_height !== brand.logo_height ||
    local.sort_order !== brand.sort_order ||
    JSON.stringify([...(local.service_paths || [])].sort()) !==
      JSON.stringify([...(brand.service_paths || [])].sort());

  function toggleServicePath(path: string) {
    const current = local.service_paths || [];
    const has = current.includes(path);
    const next = has ? current.filter((p) => p !== path) : [...current, path];
    // Keep primary service_path in sync: first selected, or unchanged if none.
    const nextPrimary = next.includes(local.service_path)
      ? local.service_path
      : next[0] ?? local.service_path;
    setLocal({ ...local, service_paths: next, service_path: nextPrimary });
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="grid gap-4 md:grid-cols-[120px_1fr_auto] md:items-center">
        <div className="flex h-20 items-center justify-center rounded bg-white">
          {local.logo_url ? (
            <img
              src={local.logo_url}
              alt={local.name}
              style={{ height: local.logo_height, maxHeight: 60 }}
              className="max-w-full object-contain"
            />
          ) : (
            <div className="flex flex-col items-center text-xs text-muted-foreground">
              <ImageOff className="h-5 w-5" />
              <span className="mt-1">No logo</span>
            </div>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Name</Label>
            <Input
              value={local.name}
              onChange={(e) => setLocal({ ...local, name: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Slug</Label>
            <Input
              value={local.slug}
              onChange={(e) => setLocal({ ...local, slug: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Service pages (select one or more)</Label>
            <div className="mt-1 grid max-h-40 grid-cols-1 gap-1 overflow-y-auto rounded-md border border-input bg-background p-2 sm:grid-cols-2">
              {SERVICE_PATHS.map((p) => {
                const checked = (local.service_paths || []).includes(p);
                const isPrimary = p === local.service_path;
                return (
                  <label
                    key={p}
                    className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleServicePath(p)}
                      className="h-4 w-4 rounded border-input"
                    />
                    <span className="flex-1 truncate">{p}</span>
                    {checked && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setLocal({ ...local, service_path: p });
                        }}
                        className={`rounded px-1.5 py-0.5 text-[10px] ${isPrimary ? "bg-brand text-brand-foreground" : "border border-input hover:bg-muted"}`}
                        title="Set as primary link target"
                      >
                        {isPrimary ? "Primary" : "Set primary"}
                      </button>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Logo height (px)</Label>
              <NumberInput
                min={1}
                value={local.logo_height}
                onValueChange={(n) => setLocal({ ...local, logo_height: n || 28 })}
              />
            </div>
            <div>
              <Label className="text-xs">Sort order</Label>
              <NumberInput
                value={local.sort_order}
                onValueChange={(n) => setLocal({ ...local, sort_order: n })}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 md:items-end">
          <div className="flex items-center gap-2">
            <Switch
              checked={brand.enabled}
              onCheckedChange={(v) => onUpdate({ enabled: v })}
            />
            <span className="text-xs text-muted-foreground">
              {brand.enabled ? "Visible" : "Hidden"}
            </span>
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={brand.include_on_letter}
              onChange={(e) => onUpdate({ include_on_letter: e.target.checked })}
              className="h-4 w-4 rounded border-input"
            />
            Include on homeowner letter
          </label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.currentTarget.value = "";
            }}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
          >
            <Upload className="mr-2 h-4 w-4" />
            {local.logo_url ? "Replace logo" : "Upload logo"}
          </Button>
          {local.logo_url && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onUpdate({ logo_url: null })}
              disabled={busy}
            >
              Remove logo
            </Button>
          )}
          <Button
            size="sm"
            onClick={async () => {
              const ok = await onUpdate({
                name: local.name,
                slug: local.slug,
                service_path: local.service_path,
                service_paths: local.service_paths,
                logo_height: local.logo_height,
                sort_order: local.sort_order,
              });
              if (ok) toast.success("Saved");
            }}
            disabled={!dirty || busy}
          >
            <Save className="mr-2 h-4 w-4" /> Save
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} disabled={busy}>
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </Button>
        </div>
      </div>
    </div>
  );
}
